"use client";

import { splitPhoneByCountryCode } from "@/app/(ar)/contact/ContactPageClient";
import { BUSINESS_UNIT_ID, TENANT_ID } from "@/config/api";
import type { AuthUser } from "@/lib/authSession";
import { buildLeadAttributionFields } from "@/lib/leadAttribution";
import {
  getStoredUtmChannelAttribution,
  LEAD_CLICK_ID_KEYS,
  resolveLeadClickIds,
} from "@/lib/utmChannelCookies";

type LeadCreateBody = {
  customerId: string;
  leadEvent: "FORM_SUBMITTED";
  category: "Service" | "Package" | "Product" | "Offer";
  leadTypeId: number;
  pageType:
    | "Service Detail"
    | "Service Listing"
    | "Package Detail"
    | "Package Listing"
    | "Product Detail"
    | "Product Listing"
    | "Offer Listing"
    | "Offer Detail";
  typeId: string;
  verified: 1 | 0;
  email?: string;
  customerName?: string;
  phoneCode?: string;
  phoneNumber?: string;
  cityId?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  gclid?: string;
  consent: boolean;
  accept: boolean;
  city?: string;
  leadSource: "Organic" | "In organic";
  leadSubSource?: string;
  platform?: "Web" | "WAP" | "Android" | "IOS";
};

export type LeadContext = {
  category: LeadCreateBody["category"];
  pageType: LeadCreateBody["pageType"];
  typeId: string;
  /** Defaults to 1 if omitted. */
  leadTypeId?: number;
};
/**
 * When `LoginDialog` opens without a page-specific {@link LeadContext} (e.g. header “Login”),
 * lead/create still uses this payload so the API is called. Same type/city as home service cards.
 */
export const DEFAULT_LOGIN_LEAD_CONTEXT: LeadContext = {
  category: "Service",
  pageType: "Service Listing",
  leadTypeId: 1,
  // typeId: "25cc14b6-d1a2-41d3-bd2c-95d7ae453600",
  typeId: "",
};


async function postLeadCreateViaProxy(
  body: LeadCreateBody,
  headers: { tenantId: string; businessUnitId: string }
): Promise<boolean> {
  try {
    const res = await fetch("/api/lead/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...body,
        _tenantId: headers.tenantId,
        _businessUnitId: headers.businessUnitId,
      }),
      cache: "no-store",
    });
    return res.ok;
  } catch {
    return false;
  }
}

const UTM_LEAD_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export async function createLeadCreateEvent(args: {
  context: LeadContext;
  verified: 0 | 1;
  phoneCode: string;
  phoneNumber: string;
  /**
   * Prefer passing `user` when verified=1 so tenant/businessUnit/customer fields can be derived.
   * When verified=0, omit user and pass blank customer fields.
   */
  user?: AuthUser | null;
  customerId?: string;
  email?: string;
  customerName?: string;
  tenantId?: string;
  businessUnitId?: string;
  city?: string;
  /** From `lastTouchUtmParams` + stored channel cookies; non-empty values are forwarded. */
  utmParams?: Record<string, string>;
  consent: boolean;
  accept: boolean;
  cityId?: string;
}): Promise<boolean> {
  const tenantId = (args.tenantId || args.user?.tenantId || TENANT_ID).trim();
  const businessUnitId = (args.businessUnitId || args.user?.businessUnitId || BUSINESS_UNIT_ID).trim();

  const customerId =
    (args.customerId ?? args.user?.customerId ?? "").trim();

  let utmParams = args.utmParams;
  if (!utmParams) {
    try {
      const { last } = getStoredUtmChannelAttribution();
      utmParams = {
        utm_source: last.source,
        utm_medium: last.medium,
        utm_campaign: last.campaign,
        utm_content: last.content,
        utm_term: last.term,
      };
    } catch {
      utmParams = undefined;
    }
  }

  const attribution = buildLeadAttributionFields({ utmParams });

  const body: LeadCreateBody = {
    customerId,
    leadEvent: "FORM_SUBMITTED",
    category: args.context.category,
    leadTypeId: args.context.leadTypeId ?? 1,
    pageType: args.context.pageType,
    typeId: args.context.typeId,
    verified: args.verified,
    leadSource: attribution.leadSource,
    ...(attribution.leadSubSource
      ? { leadSubSource: attribution.leadSubSource }
      : {}),
    platform: attribution.platform,
    email: (args.email ?? "").trim(),
    customerName: (args.customerName ?? args.user?.name ?? "").trim(),
    phoneCode: args.phoneCode,
    phoneNumber: args.phoneNumber,
    cityId: args.cityId?.toString() ?? "",
    consent: args.consent,
    accept: args.accept,
    city: args.city,
  };

  if (utmParams) {
    for (const k of UTM_LEAD_KEYS) {
      const v = utmParams[k]?.trim();
      if (v) body[k] = v;
    }
  }

  let clickIds: Partial<Record<(typeof LEAD_CLICK_ID_KEYS)[number], string>> =
    {};
  try {
    const { last } = getStoredUtmChannelAttribution();
    clickIds = resolveLeadClickIds(last);
  } catch {
    clickIds = resolveLeadClickIds();
  }
  for (const k of LEAD_CLICK_ID_KEYS) {
    const v = clickIds[k]?.trim();
    if (v) body[k] = v;
  }

  return await postLeadCreateViaProxy(body, { tenantId, businessUnitId });
}

/**
 * Fire a Lead create event when a logged-in user clicks Find Workshop from a Service Detail page.
 * This is intentionally best-effort (no throw) to avoid blocking navigation.
 */
export async function createServiceDetailFindWorkshopLead(args: {
  user: AuthUser;
  typeId: string;
  cityId: string;
  city: string;
}): Promise<void> {
  const customerId = args.user.customerId?.trim();
  if (!customerId) return;

  const { phoneCode, phoneNumber } = splitPhoneByCountryCode(args.user.phone || "");
  await createLeadCreateEvent({
    context: {
      category: "Service",
      pageType: "Service Detail",
      typeId: args.typeId,
    },
    verified: 1,
    user: args.user,
    customerId,
    phoneCode,
    phoneNumber,
    email: args.user.email?.trim() || "NA",
    customerName: args.user.name?.trim() || "NA",
    consent: true,
    accept: true,
    cityId: args.cityId?.toString() ?? "",
    city: args.city,
  });
}

/**
 * Fire a Lead create event when a logged-in user clicks Find Workshop from a Package Detail page.
 * Best-effort (no throw) so it never blocks navigation.
 */
export async function createPackageDetailFindWorkshopLead(args: {
  user: AuthUser;
  packageTypeId: string;
  cityId?: number | string;
  city?: string;
}): Promise<void> {
  const customerId = args.user.customerId?.trim();
  if (!customerId) return;

  const { phoneCode, phoneNumber } = splitPhoneByCountryCode(args.user.phone || "");
  await createLeadCreateEvent({
    context: {
      category: "Package",
      pageType: "Package Detail",
      typeId: args.packageTypeId,
    },
    verified: 1,
    user: args.user,
    customerId,
    phoneCode,
    phoneNumber,
    email: args.user.email?.trim() || "NA",
    customerName: args.user.name?.trim() || "NA",
    consent: true,
    accept: true,
    cityId: args.cityId?.toString() ?? "",
    city: args.city,
  });
}

/**
 * Fire a Lead create event when a logged-in user clicks Find Workshop from a Product Detail page.
 * Best-effort (no throw) so it never blocks navigation.
 */
export async function createProductDetailFindWorkshopLead(args: {
  user: AuthUser;
  productId: string;
  cityId?: number | string;
  city?: string;
}): Promise<void> {
  const customerId = args.user.customerId?.trim();
  if (!customerId) return;

  const { phoneCode, phoneNumber } = splitPhoneByCountryCode(args.user.phone || "");
  await createLeadCreateEvent({
    context: {
      category: "Product",
      pageType: "Product Detail",
      typeId: args.productId,
    },
    verified: 1,
    user: args.user,
    customerId,
    phoneCode,
    phoneNumber,
    email: args.user.email?.trim() || "NA",
    customerName: args.user.name?.trim() || "Customer",
    consent: true,
    accept: true,
    cityId: args.cityId?.toString() ?? "",
    city: args.city,
  });
}

